/**
 * CR316: Render Status Tracking for V2 Blocks
 * 
 * Provides a context for collecting render status from V2 blocks.
 * Each block reports its render status (rendered, toggle_off, no_data, error)
 * which is then displayed in the V2 Debug Inspector.
 */

import { createContext, useContext } from "react";
import type { DealCardSurfaceKey } from "./types";

// ================================================================
// TYPES
// ================================================================

export type BlockRenderReason = 
  | "toggle_off"     // Block is disabled via toggle
  | "no_data"        // Block returned null due to missing data
  | "error"          // Block threw an error during render
  | "placeholder"    // Block is a placeholder (not extracted yet)
  | "rendered";      // Block rendered successfully

export interface BlockRenderStatus {
  /** Block ID */
  blockId: string;
  /** Block type */
  blockType: string;
  /** Surface this block belongs to */
  surface: DealCardSurfaceKey;
  /** Whether the block is effectively enabled (toggle state) */
  effectiveEnabled: boolean;
  /** Whether the block actually rendered content */
  rendered: boolean;
  /** Reason for render status */
  reason: BlockRenderReason;
  /** Optional error message if reason is 'error' */
  errorMessage?: string;
}

export interface RenderStatusCollection {
  primary: BlockRenderStatus[];
  details: BlockRenderStatus[];
}

// ================================================================
// CONTEXT
// ================================================================

export interface RenderStatusCollectorContextValue {
  /** Report a block's render status */
  reportStatus: (status: BlockRenderStatus) => void;
  /** Get collected statuses */
  getStatuses: () => RenderStatusCollection;
  /** Whether collection is active */
  isCollecting: boolean;
}

const defaultContext: RenderStatusCollectorContextValue = {
  reportStatus: () => {},
  getStatuses: () => ({ primary: [], details: [] }),
  isCollecting: false,
};

export const RenderStatusCollectorContext = createContext<RenderStatusCollectorContextValue>(defaultContext);

export function useRenderStatusCollector() {
  return useContext(RenderStatusCollectorContext);
}

// ================================================================
// HELPERS
// ================================================================

/**
 * Create a render status object for a block
 */
export function createBlockRenderStatus(
  blockId: string,
  blockType: string,
  surface: DealCardSurfaceKey,
  enabled: boolean,
  rendered: boolean,
  reason?: BlockRenderReason,
  errorMessage?: string
): BlockRenderStatus {
  const computedReason = reason || (
    !enabled ? "toggle_off" :
    !rendered ? "no_data" :
    "rendered"
  );
  
  return {
    blockId,
    blockType,
    surface,
    effectiveEnabled: enabled,
    rendered,
    reason: computedReason,
    errorMessage,
  };
}

/**
 * Get display text for a render reason
 */
export function getRenderReasonDisplayText(reason: BlockRenderReason): string {
  switch (reason) {
    case "toggle_off":
      return "Disabled";
    case "no_data":
      return "No Data";
    case "error":
      return "Error";
    case "placeholder":
      return "Placeholder";
    case "rendered":
      return "Rendered";
    default:
      return reason;
  }
}

/**
 * Get badge color class for a render reason
 */
export function getRenderReasonBadgeClass(reason: BlockRenderReason): string {
  switch (reason) {
    case "toggle_off":
      return "bg-muted text-muted-foreground";
    case "no_data":
      return "bg-amber-500/20 text-amber-700 border-amber-300";
    case "error":
      return "bg-destructive/20 text-destructive border-destructive/30";
    case "placeholder":
      return "bg-purple-500/20 text-purple-700 border-purple-300";
    case "rendered":
      return "bg-green-500/20 text-green-700 border-green-300";
    default:
      return "bg-muted text-muted-foreground";
  }
}
